import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { getActiveSpan, getRootSpan } from '@sentry/angular';
import { filter } from 'rxjs';
import { BASKET_PATH } from '../../app.routes';

/**
 * Segments that ROUTING_TABLE declares statically. Sentry already derives a useful transaction name
 * from the matched route config for these, so they are left alone.
 */
const STATIC_ROUTE_SEGMENTS = new Set(['_library', '_icmslibrary', 'account', 'login', BASKET_PATH, 'checkout', 'search', 'error']);

/**
 * Collapses a URL to a bounded route pattern by keeping the first segment and parameterising the
 * rest: `/product/alphasorb-acoustic-panel-room-kit` becomes `/product/:slug`,
 * `/product-category/acoustic-panels/fabric-wrapped-panels` becomes
 * `/product-category/:slug/:slug`, and single segment landing pages such as `/about-us` keep their
 * own name because that set is small and worth seeing individually.
 *
 * Naming per URL instead would mint a transaction for every product in the catalogue, and unbounded
 * cardinality is what makes performance data unusable in the other direction.
 */
export const parameterisePath = (pathname: string): string => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) {
        return '/';
    }
    return '/' + [segments[0], ...segments.slice(1).map(() => ':slug')].join('/');
};

/**
 * Names pageload and navigation transactions after the URL rather than the matched route.
 *
 * Every catalogue URL — the homepage, category listings, product pages and CMS landing pages — is
 * resolved through the `**` route in ROUTING_TABLE, because those paths are authored in the CMS and
 * cannot be enumerated statically. Sentry names a transaction after the route config it matched, so
 * all of them arrive as `/**\/` and collapse into a single bucket. That is why the Web Vitals module
 * cannot say which page is shifting: on 2026-08-05 every pageload carrying a CLS measurement in
 * project 71 shared the one transaction name, so no amount of extra sampling would have localised
 * the problem.
 *
 * Renaming the root span rather than the transaction event matters: Sentry 26.x ingests spans first
 * and the Web Vitals module reads the spans dataset, so a rename applied only in
 * beforeSendTransaction would not reach the data those charts are built from.
 */
export const provideSentryTransactionNames = (): EnvironmentProviders =>
    provideAppInitializer(() => {
        // Tracing is browser-only, and app initializers also run during SSR.
        if (typeof window === 'undefined') {
            return;
        }
        const router = inject(Router);
        router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(event => {
            const active = getActiveSpan();
            const root = active && getRootSpan(active);
            if (!root) {
                return;
            }
            const { pathname } = new URL(event.urlAfterRedirects, window.location.origin);
            if (STATIC_ROUTE_SEGMENTS.has(pathname.split('/').filter(Boolean)[0] ?? '')) {
                return;
            }
            root.updateName(parameterisePath(pathname));
            // Marks the name as a parameterised route so Sentry does not treat it as a raw URL and
            // re-derive its own. The literal avoids importing the constant from @sentry/core, which
            // is not part of the surface this app already uses.
            root.setAttribute('sentry.source', 'route');
        });
    });
